<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { statusWord } from '../severity.js'
import ModeBadge from './ModeBadge.vue'
import { modeOfRow, modesOf } from '../modes.js'
import { CHUNK_ROWS, FIRST_ROWS, groupKeyOf, groupRows, drawBudget, isOpen, isSmall, mainKey, readOpenChoices, summaryOf, writeOpenChoice } from '../line-groups.js'

export type LineStatus = 'good' | 'delayed' | 'planned' | 'suspended'

export interface LedgerLine {
  /** Subscription id, not the PTV route id. */
  id: string
  name: string
  /** The line's official PTV colour. Comes from data — never hardcode it here. */
  colour: string
  status: LineStatus
  /** Minutes late. Null until departures data is wired up; the row falls back to a word. */
  delayMinutes?: number | null
  /** One short line of context, e.g. "Signal fault at Caulfield". */
  note?: string
  /** PTV route id, carried so a tap can resolve the line without a lookup. */
  routeId?: number
  /** The disruption this row leads to, if it has one. */
  disruptionId?: string | null
  /** V/Line rather than Metro. Read by the sort that groups them, not by the row. */
  regional?: boolean
  /** Coaches or buses standing in, or a tram diverted. Changes the word, not the rank. */
  replacement?: 'coaches' | 'buses' | 'diverted' | null
  /** The mode id of a route of a mode with a badge of its own ('tram'). Absent on every train row. */
  mode?: string
  /** The route number, for its badge. */
  short?: string
  /** A route's two ends, "East Brunswick ↔ St Kilda Beach", printed beside the badge. */
  ends?: string
}

const props = withDefaults(
  defineProps<{
    lines: LedgerLine[]
    /** Every line the reader does not watch. Same rows, one step quieter. */
    others?: LedgerLine[]
    emptyMessage?: string
    /** Lines are watched but the mode chips hide them all: not the empty state. */
    filteredOut?: boolean
    /** The city publishes nothing live: a good row says TIMETABLE, not a dash. */
    timetableOnly?: boolean
    /** The city, for its modes and for remembering which groups are open. */
    city?: string
    /** The city's names for its train groups: `{ train, regional }`. */
    groupNames?: { train?: string; regional?: string; main?: string }
  }>(),
  {
    emptyMessage: 'No lines yet. Add the ones you actually catch.',
    others: () => [],
    filteredOut: false,
    timetableOnly: false,
    city: '',
    groupNames: () => ({}),
  },
)

const emit = defineEmits<{
  (e: 'select', line: LedgerLine): void
  (e: 'add'): void
}>()

const isEmpty = computed(() => props.lines.length === 0 && !props.filteredOut)

/**
 * One ledger: the lines somebody watches, then the rest of the network.
 *
 * The whole network is here rather than only the watched lines, because the
 * question "is anything up with the trains" is asked of a line nobody watches
 * about once a week — a visitor's line, the one a friend is on. What keeps it
 * from becoming a list of seventy equal rows is contrast (the watched section
 * is in enamel, the rest in dim) and GROUPS: the rest is one collapsible group
 * per kind of service — metro, V/Line, trams — each header saying how many
 * lines it holds and what is wrong on them, so a group that is fine costs one
 * line and a group that is not opens itself. See src/line-groups.js.
 */
const watchedRows = computed(() => props.lines)

const groups = computed(() => groupRows(props.others, {
  names: props.groupNames,
  modes: modesOf(props.city),
}))
const main = computed(() => mainKey(groups.value, (props.groupNames as { main?: string }).main))

// Only what somebody chose is remembered (per city and group); everything
// else follows the rule, so a line going wrong still opens its group.
const choices = ref(readOpenChoices())
const openOf = (g: { key: string; rows: LedgerLine[] }) => isOpen(choices.value, props.city, g, main.value)
function toggleGroup(g: { key: string; rows: LedgerLine[] }) {
  choices.value = writeOpenChoice(props.city, g.key, !openOf(g))
}
const summary = (g: { rows: LedgerLine[] }) => summaryOf(g.rows, { timetableOnly: props.timetableOnly })
/** Whether a header has something to say in full voice: anything but a clear or planned-only group. */
const troubled = (g: { rows: LedgerLine[] }) => g.rows.some((r) => ['delayed', 'suspended'].includes(r.status))
// "24 ROUTES" for the trams: the count uses the group's own mode's word for a
// line (trains stay LINE and LINES, which is what `labels` falls back to).
// A group's size is ALL its lines, the watched ones included: a watched line
// leaves the group's rows but not the network, and Choose lines counts the same.
const sizeOf = (g: { key: string; rows: LedgerLine[] }) => g.rows.length
  + props.lines.filter((l) => groupKeyOf(l) === g.key).length
const countOf = (g: { key: string; rows: LedgerLine[] }) => {
  const labels = modesOf(props.city).find((m: any) => m.id === g.key)?.labels
  const n = sizeOf(g)
  const word = n === 1 ? (labels?.line ?? 'line') : (labels?.lines ?? 'lines')
  return `${n} ${word.toUpperCase()}`
}

const WORDS: Record<LineStatus, string> = {
  good: 'Good service',
  delayed: 'Delayed',
  planned: 'Planned works',
  suspended: 'Suspended',
}

// ── First paint ──
// A full network is hundreds of rows (every bus route). The first screenful is
// drawn at once and the rest follows in chunks while the browser is idle; a
// shut group draws none until it is opened.
const budget = ref(FIRST_ROWS)
const openRows = computed(() => groups.value.reduce((n, g) => n + (openOf(g) ? g.rows.length : 0), 0))
const drawn = computed(() => drawBudget(groups.value.map((g) => ({ key: g.key, rows: g.rows, open: openOf(g) })), budget.value))
const rowsOf = (g: { key: string }) => drawn.value[g.key] ?? []
const later = (fn: () => void) => ((window as any).requestIdleCallback ? (window as any).requestIdleCallback(fn, { timeout: 120 }) : requestAnimationFrame(fn))
let growing = false
function grow() {
  if (budget.value >= openRows.value) { growing = false; return }
  budget.value += CHUNK_ROWS
  later(grow)
}
watch(openRows, () => { if (!growing && budget.value < openRows.value) { growing = true; later(grow) } }, { immediate: true })

function hasDelta(line: LedgerLine): boolean {
  return line.status === 'delayed' && typeof line.delayMinutes === 'number' && line.delayMinutes > 0
}

function wordOf(line: LedgerLine): string {
  return (
    statusWord({ status: line.status, replacement: line.replacement }, { live: !props.timetableOnly }) ||
    WORDS[line.status]
  )
}

/**
 * The mode a row is badged by, or null for a row with a colour bar. A mode
 * whose badge style is `number` wears its route number where a train row wears
 * its colour; the registry says which (src/modes.js).
 */
function numbered(line: LedgerLine) {
  const mode = modeOfRow({ routeId: line.routeId, mode: line.mode })
  return mode && mode.badge.style === 'number' ? mode : null
}

function screenReaderLabel(line: LedgerLine): string {
  if (hasDelta(line)) return `${line.name}, running ${line.delayMinutes} minutes late. ${line.note ?? ''}`
  return `${line.name}, ${wordOf(line).toLowerCase()}. ${line.note ?? ''}`
}
</script>

<template>
  <div v-if="isEmpty" class="empty">
    <p class="empty-copy">{{ emptyMessage }}</p>
    <button type="button" class="empty-cta" @click="emit('add')">Add lines</button>
  </div>

  <!-- Mono caps, the same voice as QUIET 22:00-06:00 and the tab bar: a
       label on the ledger, not a heading over a new screen. Hidden visually
       over the first run, which sits directly under the page's own title. -->
  <template v-if="watchedRows.length">
    <h2 class="section-head section-head-first sr-only">Your lines</h2>
    <ul class="ledger" role="list">
      <li v-for="line in watchedRows" :key="line.id" class="ledger-item">
        <button
          type="button"
          class="row"
          :class="{ 'row-tram': numbered(line) !== null }"
          :style="numbered(line) ? undefined : { borderLeftColor: line.colour }"
          :aria-label="screenReaderLabel(line)"
          @click="emit('select', line)"
        >
          <ModeBadge v-if="numbered(line)" :mode="numbered(line)!" :number="line.short ?? ''" />
          <span class="row-main">
            <span class="row-name" :class="{ 'row-ends': numbered(line) !== null }">{{ numbered(line) ? (line.ends || line.name) : line.name }}</span>
            <span v-if="line.note" class="row-note">{{ line.note }}</span>
          </span>
          <span class="row-status" aria-hidden="true">
            <template v-if="hasDelta(line)">
              <span class="delta">+{{ line.delayMinutes }}</span>
              <span class="delta-unit">MIN</span>
            </template>
            <span v-else-if="line.status === 'good' && !timetableOnly" class="clear">&mdash;</span>
            <span v-else class="word" :class="{ 'word-signal': line.status === 'suspended' }">
              {{ wordOf(line).toUpperCase() }}
            </span>
          </span>
        </button>
      </li>
    </ul>
    <!-- Between the person's own lines and the rest of the network: the
         place for something said to them about their lines, once. -->
    <slot name="after-watched" />
  </template>

  <template v-if="groups.length">
    <h2 class="section-head" :class="{ 'section-head-first': !watchedRows.length && !isEmpty }">Other lines</h2>

    <section v-for="g in groups" :key="g.key" class="group" :data-group="g.key">
      <!-- A real button, 48px tall, that says what is inside before it is
           opened: the count and the worst thing on it. -->
      <h3 v-if="isSmall(g)" class="group-title">
        <span class="group-head group-head-static">
          <span class="group-main">
            <span class="group-name">{{ g.label }}</span>
            <span class="group-count">{{ countOf(g) }}</span>
          </span>
          <span class="group-summary" :class="{ 'group-summary-trouble': troubled(g) }">{{ summary(g) }}</span>
        </span>
      </h3>
      <h3 v-else class="group-title">
        <button
          type="button"
          class="group-head"
          :aria-expanded="openOf(g)"
          :aria-controls="`lines-group-${g.key}`"
          @click="toggleGroup(g)"
        >
          <span class="group-main">
            <span class="group-name">{{ g.label }}</span>
            <span class="group-count">{{ countOf(g) }}</span>
          </span>
          <span class="group-summary" :class="{ 'group-summary-trouble': troubled(g) }">{{ summary(g) }}</span>
          <svg class="chevron" :class="{ 'chevron-open': openOf(g) }" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" />
          </svg>
        </button>
      </h3>
      <div :id="`lines-group-${g.key}`" class="group-body" :class="{ 'group-body-open': openOf(g) }" :inert="!openOf(g)">
        <div class="group-inner">
          <ul class="ledger" role="list">
            <li v-for="line in rowsOf(g)" :key="line.id" class="ledger-item">
              <!-- A TRAM ROW wears its number where a train row wears its colour:
                   the badge, then the route's two ends. Everything right of that
                   is the same row, the same status column, the same tap. -->
              <button
                type="button"
                class="row row-quiet"
                :class="{ 'row-tram': numbered(line) !== null }"
                :style="numbered(line) ? undefined : { borderLeftColor: line.colour }"
                :aria-label="screenReaderLabel(line)"
                @click="emit('select', line)"
              >
                <ModeBadge v-if="numbered(line)" :mode="numbered(line)!" :number="line.short ?? ''" />
                <span class="row-main">
                  <span class="row-name" :class="{ 'row-ends': numbered(line) !== null }">{{ numbered(line) ? (line.ends || line.name) : line.name }}</span>
                  <span v-if="line.note" class="row-note">{{ line.note }}</span>
                </span>
                <span class="row-status" aria-hidden="true">
                  <template v-if="hasDelta(line)">
                    <span class="delta">+{{ line.delayMinutes }}</span>
                    <span class="delta-unit">MIN</span>
                  </template>
                  <span v-else-if="line.status === 'good' && !timetableOnly" class="clear">&mdash;</span>
                  <span v-else class="word" :class="{ 'word-signal': line.status === 'suspended' }">
                    {{ wordOf(line).toUpperCase() }}
                  </span>
                </span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </template>
</template>

<style scoped>
/* The section label. Mono, 10px, dim — the app's caps voice. */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.section-head {
  margin: 26px 0 8px;
  padding: 0 18px;
  font-family: var(--la-font-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--la-dim);
}

/* ── The groups ── */
.group { border-bottom: 0.5px solid var(--la-rule); }
.group-title {
  margin: 0;
  font: inherit;
  /* The heading of the group you are in stays under the top of the screen. */
  position: sticky;
  top: env(safe-area-inset-top);
  z-index: 2;
  background: var(--la-carriage);
}

/* The header is the whole 48px row and the whole tap. */
.group-head {
  width: 100%;
  min-height: 48px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: "main chev" "sum chev";
  align-items: center;
  column-gap: 12px;
  row-gap: 2px;
  padding: 8px 18px;
  background: none;
  border: 0;
  border-top: 0.5px solid var(--la-rule);
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
.group-head:hover, .group-head:active { background: var(--la-platform-hover); }
/* Dim text is 4.5:1 on the page, but not on the pressed ground in the dark
   theme, so it steps up to full ink while pressed. */
.group-head:hover .group-count, .group-head:active .group-count,
.group-head:hover .group-summary, .group-head:active .group-summary,
.group-head:hover .chevron, .group-head:active .chevron { color: var(--la-enamel); }
.group-head-static { cursor: default; grid-template-areas: "main" "sum"; grid-template-columns: 1fr; }
.group-head:focus-visible { outline: 2px solid var(--la-enamel); outline-offset: -3px; }
.group-main { grid-area: main; display: flex; align-items: baseline; gap: 10px; }
.group-name {
  font-family: var(--la-font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  color: var(--la-enamel);
}
.group-count { font-family: var(--la-font-mono); font-size: 11px; letter-spacing: 0.06em; color: var(--la-dim); }
.group-summary { grid-area: sum; font-family: var(--la-font-mono); font-size: 11px; letter-spacing: 0.06em; color: var(--la-dim); }
.group-summary-trouble { color: var(--la-enamel); }
.chevron { grid-area: chev; color: var(--la-dim); transition: transform 160ms ease; }
.chevron-open { transform: rotate(180deg); }
.chevron-go { transform: none; }

/* Open and shut by height, in 160ms. A shut group is also hidden and inert,
   so nothing in it can be tabbed to or read out. */
.group-body {
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  transition: grid-template-rows 160ms ease, visibility 0s linear 160ms;
}
.group-body-open {
  grid-template-rows: 1fr;
  visibility: visible;
  transition: grid-template-rows 160ms ease, visibility 0s;
}
.group-inner { min-height: 0; overflow: hidden; }

/* The masthead already leaves 16px under the title; a second 26px on top of it
   opens a hole between the screen name and its first row. */
.section-head-first {
  margin-top: 2px;
}

.ledger-item {
  /* Rows off screen are not laid out or painted until they scroll near. */
  content-visibility: auto;
  contain-intrinsic-size: auto 64px;
}

.ledger {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-top: 0.5px solid var(--la-rule);
}

.row {
  width: 100%;
  min-height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 14px 14px;
  background: var(--la-platform);
  border: 0;
  border-left: 6px solid transparent;
  border-radius: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: background var(--la-dur) ease;
}

.row:hover,
.row:active {
  background: var(--la-platform-hover);
}

.row:focus-visible {
  outline: 2px solid var(--la-enamel);
  outline-offset: -3px;
}

.row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.row-name {
  font-family: var(--la-font-display);
  font-stretch: 125%;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--la-enamel);
}

.row-note {
  font-family: var(--la-font-body);
  font-size: 12px;
  line-height: 1.35;
  color: var(--la-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-status {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: var(--la-font-mono);
  font-variant-numeric: tabular-nums;
}

.delta {
  font-size: 24px;
  font-weight: 500;
  line-height: 1;
  color: var(--la-signal);
}

.delta-unit {
  font-size: 10px;
  letter-spacing: 0.06em;
  margin-top: 4px;
  color: var(--la-dim);
}

.clear {
  font-size: 24px;
  line-height: 1;
  color: var(--la-enamel);
}

.word {
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--la-dim);
}

.word-signal {
  color: var(--la-signal);
}

/* A route's two ends are two names, and at the display face's full width most
   of them wrap. The face's normal width keeps most on one line; the rest wrap,
   which is better than losing an end to an ellipsis. */
.row-ends {
  font-stretch: 100%;
}

/* A tram row: no colour bar, the badge instead, and the same inset the bar
   gave a train row's name so the two kinds of row line up down the ledger. */
.row-tram {
  border-left: 0;
  padding-left: 20px;
}

/* Unwatched lines: same anatomy, same colour rule, one step of contrast down.
   The name drops from enamel to dim and the em dash with it; the delay figure
   keeps its red, because a line in trouble is in trouble whether or not this
   reader catches it. Nothing here goes below the dim/platform contrast the
   rest of the app already ships — quieter is a step, not a fade. */
.row-quiet .row-name {
  color: var(--la-dim);
  font-weight: 400;
}

.row-quiet .clear {
  color: var(--la-dim);
}

.empty {
  padding: 48px 24px;
  text-align: center;
}

.empty-copy {
  margin: 0 0 20px;
  font-family: var(--la-font-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--la-dim);
}

.empty-cta {
  min-height: 44px;
  padding: 0 20px;
  background: transparent;
  color: var(--la-enamel);
  font-family: var(--la-font-body);
  font-size: 14px;
  border: 0.5px solid var(--la-rule);
  border-radius: 6px;
  cursor: pointer;
}

.empty-cta:focus-visible {
  outline: 2px solid var(--la-enamel);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .row,
  .chevron,
  .group-body,
  .group-body-open {
    transition: none;
  }
}
</style>

